import {
  actionColumn,
  ButtonMenu,
  Chips,
  column,
  ContentHeader,
  Css,
  FilterDefs,
  GridCellContent,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  IconButton,
  InlineFeedbackBanner,
  MenuItem,
  multiFilter,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  useComputed,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { type FieldState, type ObjectConfig, type ObjectState, useFormState } from "@homebound/form-state";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { AiProposalValue, pendingSources } from "src/components/formFields/ai/AiProposalValue";
import { BoundAiTextField } from "src/components/formFields/ai/BoundAiTextField";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  aiProposalsConfig,
  type AiProposalsFormValue,
  entityIsRemoved,
  flattenEntityProposals,
  hasPendingProposals,
  indexEntityProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { type AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { findAiFieldProposal, parseAiNumber } from "src/components/formFields/ai/utils/bindAiProposal";
import { resolvePendingAiProposals } from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  AiEntityProposalType,
  AiFieldProposalStatus,
  FeatureFlagType,
  IncrementalCollectionOp,
  Maybe,
  SaveReadyPlanOptionInput,
  SaveReadyPlanProgramDataInput,
} from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageOptionsV2_OptionFragment,
  PlanPackageOptionsV2_PlanPackageFragment,
  PlanPackageOptionsV2Query,
  usePlanPackageOptionsV2Query,
  useSavePlanPackageOptionsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import { planPackageAiSourcesRoute } from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";
import { openNewTab } from "src/utils/window";
import { AddOptionsModal } from "./options/AddOptionsModal";
import { EditOptionModal } from "./options/EditOptionModal";
import { optionSqftFields } from "./options/ProgramDataImpactsSection";

type OptionsTabProps = { planPackageId: string; versionId: string };

export function OptionsTab(props: OptionsTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageOptionsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, enumDetails }) => (
    <DataView planPackage={planPackage} enumDetails={enumDetails} />
  ));
}

type DataViewProps = {
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  enumDetails: PlanPackageOptionsV2Query["enumDetails"];
};
type OptionRow = {
  rpo: PlanPackageOptionsV2_OptionFragment;
  form: ObjectState<OptionRowValue>;
  aep: AiFormAepFragment | undefined;
  fieldProposals: ObjectState<AiFieldProposalFormValue>[];
};
type Row = SimpleHeaderAndData<OptionRow>;
type OptionsFilter = { type?: string[]; location?: Maybe<string>[] };
type ReviewDecision = AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected;
type OptionRowValue = {
  id: string;
  code: Maybe<string>;
  name: Maybe<string>;
  location: Maybe<string>;
  /** Formatted impact text; Sellable includes both above- and below-ground square footage. */
  sellableSqft: Maybe<string>;
  netBuildableSqft: Maybe<string>;
  grossBuildableSqft: Maybe<string>;
};
type OptionsFormValue = AiProposalsFormValue & { options: OptionRowValue[] };

function DataView(props: DataViewProps) {
  const { planPackage, enumDetails } = props;
  const { openModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageOptionsV2Mutation();
  const aiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);
  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const filterDefs = useMemo(() => createFilterDefs(planPackage.options), [planPackage]);
  const layoutState = useGridTableLayoutState<OptionsFilter>({
    persistedFilter: { filterDefs, storageKey: `${planPackage.id}_planOptionsFilter` },
    search: "client",
  });
  const { filter } = layoutState;
  const [addedOptionIds, setAddedOptionIds] = useState<string[]>([]);

  // The import creates a CREATE proposal's option up front, so every proposal keys to an existing row.
  const optionProposals = useMemo(
    () => indexEntityProposals(aiEnabled ? planPackage.aiProposal?.entityProposals : undefined, "ReadyPlanOption"),
    [planPackage, aiEnabled],
  );
  /**
   * Holds the option values for read-only bound fields and the proposals' live review statuses.
   * Server refreshes update the displayed values; review decisions supply the mutation payload.
   *
   * Deliberately no `autoSave`: every review action is a discrete click, so `reviewProposals` saves
   * right there. Nothing on this tab writes on its own.
   */
  const optionsForm = useFormState({
    config: optionsConfig,
    init: { input: planPackage, map: (pp) => mapToForm(pp, aiEnabled) },
  });

  /** Each option's entity proposal paired with its live statuses, shared by the cells, rows, and saves. */
  const proposalsByOptionId = useComputed(
    () => groupProposalsByOption(optionProposals, optionsForm.fieldProposals.rows),
    [optionsForm, optionProposals],
  );

  /** Persists this batch's decisions, plus whatever those decisions write to the options themselves. */
  const saveOptions = useCallback(async () => {
    const proposals = toResolveInputs(optionsForm.fieldProposals.changedValue);
    if (proposals.isEmpty) return;
    // Options decided in an earlier save still read as accepted, so skip them and re-send nothing.
    const changedIds = new Set(proposals.map(({ id }) => id));
    const decided = [...proposalsByOptionId].filter(([, { fieldProposals }]) =>
      fieldProposals.some((row) => changedIds.has(row.id.value)),
    );
    /**
     * The import proposes three shapes (see `PlanPackageAiProposal.ts` in graphql-service), and each
     * decision turns into what we write:
     *
     * - CREATE: an option the plan lacked, which the import has already built with the
     *   `globalOption`/`location`/`customName` it proposes. Accepting writes nothing, the option is
     *   already there. Rejecting it all means the user does not want it, so deactivate it.
     * - DELETE: an existing option the CDs no longer mention. Accepting deactivates it.
     * - UPDATE: an existing option with a new `customName`. Accepting writes that name, and it is the
     *   only field we write because it is the only one an UPDATE carries — a relocated option arrives
     *   as a CREATE plus a DELETE, never an UPDATE with a new `location`.
     *
     * Both ways of deactivating are the same write, so `entityIsRemoved` catches them up front.
     */
    const options = decided.flatMap(([optionId, { aep, fieldProposals }]): SaveReadyPlanOptionInput[] => {
      if (entityIsRemoved(aep.type.code, fieldProposals)) return [{ id: optionId, active: false }];
      if (aep.type.code === AiEntityProposalType.Create) return [];
      const name = fieldProposals.find((row) => row.fieldName.value === "customName");
      if (name?.status.value !== AiFieldProposalStatus.Accepted) return [];
      // A null fieldValue clears the custom name, not an empty string.
      return [{ id: optionId, name: name.fieldValue.value ?? null }];
    });
    // Square footage lives on the option's own ReadyPlanProgramData, so it rides along separately.
    const programDatas = decided.flatMap(([optionId, { fieldProposals }]): SaveReadyPlanProgramDataInput[] => {
      const option = planPackage.options.find((rpo) => rpo.id === optionId);
      if (!option) return [];
      const programData = Object.fromEntries(
        optionSqftFields.flatMap((fieldName) => {
          const proposal = fieldProposals.find((row) => row.fieldName.value === fieldName);
          if (proposal?.status.value !== AiFieldProposalStatus.Accepted) return [];
          return [[fieldName, parseAiNumber(proposal.fieldValue.value) ?? null]];
        }),
      );
      if (Object.keys(programData).isEmpty) return [];
      // A created option has no program data row of its own yet, so the accepted values create one.
      const own = optionProgramData(option);
      return [
        own
          ? { id: own.id, op: IncrementalCollectionOp.Include, programData }
          : { op: IncrementalCollectionOp.Include, readyPlanOptionIds: [option.id], programData },
      ];
    });
    const { data } = await save({
      variables: {
        input: {
          id: planPackage.id,
          ...(options.nonEmpty ? { options } : {}),
          ...(programDatas.nonEmpty ? { programDatas } : {}),
          proposals,
        },
      },
    });
    const saved = data?.savePlanPackage.planPackage;
    if (saved && saved.version.id !== planPackage.version.id) {
      // Saving a published version bumped us to a new draft, so clear dirty rows before navigating.
      optionsForm.update(saved);
      navigateToRpav(saved.version.id);
    }
  }, [optionsForm, proposalsByOptionId, planPackage, save, navigateToRpav]);

  /** Resolves proposals and persists that decision; every Accept / Ignore / Keep / Remove lands here. */
  const reviewProposals = useCallback(
    (rows: OptionProposal["fieldProposals"], decision: ReviewDecision) => {
      resolvePendingAiProposals(rows, decision);
      void saveOptions();
    },
    [saveOptions],
  );

  // Observe the array: Beam's shallow comparison does not distinguish Map entries.
  const optionForms = useComputed(
    () => optionsForm.options.rows.map((row) => [row.id.value, row] as const),
    [optionsForm],
  );
  const optionFormsById = useMemo(() => new Map(optionForms), [optionForms]);

  function onOptionsAdded(optionIds: string[]) {
    setAddedOptionIds(optionIds);
    // Automatically clear the highlight after a short delay, like the H2 schedule's last updated task
    setTimeout(() => setAddedOptionIds([]), 6_000);
  }

  const openRemoveModal = useCallback(
    (rpo: PlanPackageOptionsV2_OptionFragment) =>
      openModal({
        content: (
          <ConfirmationModal
            danger
            title="Remove Option"
            label="Remove"
            confirmationMessage={`Are you sure you want to remove ${rpo.code} - ${rpo.name} from this plan?`}
            onConfirmAction={async () => {
              const { data } = await save({
                variables: { input: { id: planPackage.id, options: [{ id: rpo.id, active: false }] } },
              });
              const versionId = data?.savePlanPackage.planPackage.version.id;
              // Removing an option on a published version triggers a new draft, so navigate to it
              if (versionId && versionId !== planPackage.version.id) navigateToRpav(versionId);
            }}
          />
        ),
      }),
    [openModal, save, planPackage, navigateToRpav],
  );

  const openEditModal = useCallback(
    (rpo: PlanPackageOptionsV2_OptionFragment) =>
      openModal({
        content: (
          <EditOptionModal
            planPackage={planPackage}
            option={rpo}
            enumDetails={enumDetails}
            aep={optionProposals.get(rpo.id)}
          />
        ),
        size: "lg",
        drawHeaderBorder: true,
      }),
    [openModal, planPackage, enumDetails, optionProposals],
  );

  // AI actions come first
  const optionMenuItems = useCallback(
    (row: OptionRow): MenuItem[] => {
      const resolve = (decision: ReviewDecision) => () => reviewProposals(row.fieldProposals, decision);
      // The viewer lists every field of the option's proposal, so any one that cites pages opens it.
      const sources = row.fieldProposals.map(pendingSources).compact().first;
      const params = { planPackageId: planPackage.id, planPackageVersionId: planPackage.version.id };
      return [
        { label: "Accept Changes", ai: true, onClick: resolve(AiFieldProposalStatus.Accepted) },
        { label: "Ignore Change", ai: true, onClick: resolve(AiFieldProposalStatus.Rejected) },
        ...(sources
          ? [
              {
                label: "View Source",
                ai: true,
                onClick: () => openNewTab(planPackageAiSourcesRoute.url(params, { afpId: sources.afpId })),
              },
            ]
          : []),
        {
          label: "Remove Option",
          onClick: () => openRemoveModal(row.rpo),
          icon: "trash",
          disabled:
            // An option the import added goes away by ignoring its proposal; removing it here would
            // deactivate the option and leave that proposal pending.
            (row.aep?.type.code === AiEntityProposalType.Create &&
              "Ignore this change to drop an option the import added") ||
            !!disabled ||
            disableBasedOnPotentialOperation(row.rpo.canDelete),
        },
      ];
    },
    [openRemoveModal, disabled, reviewProposals, planPackage],
  );

  const columns = useMemo(
    () => createColumns(disabled, openRemoveModal, optionMenuItems),
    [disabled, openRemoveModal, optionMenuItems],
  );

  const rows = useMemo(
    () => createRows(applyFilter(planPackage.options, filter), proposalsByOptionId, reviewProposals, optionFormsById),
    [planPackage, filter, proposalsByOptionId, reviewProposals, optionFormsById],
  );

  const hasPending = useComputed(() => hasPendingProposals(optionsForm.fieldProposals.rows), [optionsForm]);
  const warnings = useComputed(
    () => planPackage.options.map((rpo) => optionWarning(rpo, proposalsByOptionId)).compact(),
    [planPackage, proposalsByOptionId],
  );

  /** Accept All / Ignore All: resolve every option's proposals, then save them in one mutation. */
  function reconcileAll(decision: ReviewDecision) {
    proposalsByOptionId.forEach(({ fieldProposals }) => resolvePendingAiProposals(fieldProposals, decision));
    void saveOptions();
  }

  return (
    <div css={Css.px3.pb3.$}>
      {aiEnabled && hasPending && (
        <div css={Css.mb3.$}>
          <PlanPackageAiReviewBanner hasPending={hasPending} warnings={warnings.length} onReconcileAll={reconcileAll} />
        </div>
      )}
      <ContentHeader
        title="Plan Options"
        description="Manage the options available for this plan. Add, edit, or remove options, and define what each requires, conflicts with, or impacts."
        actions={[
          {
            label: "Add",
            icon: "plus",
            variant: "secondary",
            disabled,
            onClick: () =>
              openModal({
                content: (
                  <AddOptionsModal
                    planPackageId={planPackage.id}
                    versionId={planPackage.version.id}
                    onAdded={onOptionsAdded}
                  />
                ),
                size: { width: "xl", height: 800 },
              }),
          },
        ]}
      />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="No options yet"
        tableProps={{
          columns,
          rows,
          sorting: { on: "client" },
          rowStyles: {
            data: {
              onClick: (row) => openEditModal(row.data.rpo),
              // Highlight the rows we just added
              cellCss: (row) => Css.transition.if(addedOptionIds.includes(row.data.rpo.id)).bgBlue50.$,
            },
          },
        }}
      />
    </div>
  );
}

/** Pairs each option with its read-only form and live proposal actions. */
function createRows(
  options: PlanPackageOptionsV2_OptionFragment[],
  proposals: Map<string, OptionProposal>,
  onReview: (rows: OptionProposal["fieldProposals"], decision: ReviewDecision) => void,
  optionFormsById: Map<string, ObjectState<OptionRowValue>>,
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...options.flatMap((rpo) => {
      const form = optionFormsById.get(rpo.id);
      // New options enter the table once the form has reconciled the server's collection.
      if (!form) return [];
      const data: OptionRow = { rpo, form, ...(proposals.get(rpo.id) ?? { aep: undefined, fieldProposals: [] }) };
      return {
        kind: "data" as const,
        id: rpo.id,
        data,
        // Both callbacks read the live statuses when they run, so resolving a proposal drops the wash
        // and its banners without rebuilding rows.
        aiMode: () => hasPendingProposals(data.fieldProposals),
        companion: () => optionCompanion(data, proposals, onReview),
      };
    }),
  ];
}

/** A row gets one companion, so stack its warning and the agent's reasoning when it has both. */
function optionCompanion(
  row: OptionRow,
  proposals: Map<string, OptionProposal>,
  onReview: (rows: OptionProposal["fieldProposals"], decision: ReviewDecision) => void,
): ReactNode | undefined {
  const warning = warningCompanion(row.rpo, proposals, onReview);
  const reasoning = rationalizationCompanion(row);
  if (!warning || !reasoning) return warning ?? reasoning;
  return (
    <div css={Css.df.fdc.gap1.$}>
      {warning}
      {reasoning}
    </div>
  );
}

function rationalizationCompanion(row: OptionRow): ReactNode | undefined {
  const rationalization = row.aep?.rationalization;
  if (!rationalization || !hasPendingProposals(row.fieldProposals)) return undefined;
  return <InlineFeedbackBanner type="warning" description={rationalization} />;
}

/** The row's inline warning, whose Keep/Remove resolve the removal the warning is about. */
function warningCompanion(
  rpo: PlanPackageOptionsV2_OptionFragment,
  proposals: Map<string, OptionProposal>,
  onReview: (rows: OptionProposal["fieldProposals"], decision: ReviewDecision) => void,
): ReactNode | undefined {
  const warning = optionWarning(rpo, proposals);
  if (!warning) return undefined;
  return (
    <InlineFeedbackBanner
      type="warning"
      description={warning.message}
      actions={[
        { label: "Keep", onClick: () => onReview(warning.rows, AiFieldProposalStatus.Rejected) },
        { label: "Remove", onClick: () => onReview(warning.rows, AiFieldProposalStatus.Accepted) },
      ]}
    />
  );
}

/** Uses shared bound AI fields for option values and shared deletion display for tags and chips. */
function createColumns(
  disabled: boolean | ReactNode,
  onRemove: (rpo: PlanPackageOptionsV2_OptionFragment) => void,
  menuItems: (row: OptionRow) => MenuItem[],
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Option Code",
      data: (row) => ({
        content: (
          <BoundAiTextField
            field={row.form.code}
            aiProposal={findAiFieldProposal(row.fieldProposals, "globalOption")}
            hideAiSources
            readOnly
          />
        ),
        value: row.rpo.code,
      }),
      w: "160px",
      id: "code",
      name: "Option Code",
      canHide: false,
    }),
    column<Row>({
      header: "Option Name",
      data: (row) => ({
        content: (
          <BoundAiTextField
            field={row.form.name}
            aiProposal={findAiFieldProposal(row.fieldProposals, "customName")}
            parser={(raw) => raw ?? row.rpo.globalOption.name}
            hideAiSources
            readOnly
          />
        ),
        value: row.rpo.name,
      }),
      mw: "320px",
      id: "name",
      name: "Option Name",
      canHide: false,
    }),
    column<Row>({
      header: "Type",
      data: (row) => {
        const value = <Tag text={row.rpo.type.name} />;
        return {
          content: (
            <AiProposalValue aiProposal={findAiFieldProposal(row.fieldProposals, "type")} originalValue={value}>
              {value}
            </AiProposalValue>
          ),
          value: row.rpo.type.name,
        };
      },
      name: "Type",
      id: "type",
      canHide: true,
      initHidden: false,
    }),
    column<Row>({
      header: "Location",
      data: (row) => {
        const value = row.rpo.readyPlanLocation?.name ?? "None";
        return {
          content: (
            <BoundAiTextField
              field={row.form.location}
              aiProposal={findAiFieldProposal(row.fieldProposals, "location")}
              hideAiSources
              readOnly
            />
          ),
          value,
        };
      },
      name: "Location",
      id: "location",
      canHide: true,
      initHidden: false,
    }),
    column<Row>({
      header: "Requires",
      data: (row) => optionCodesCell(row.fieldProposals, "optionPrerequisites", row.rpo.optionPrerequisites),
      name: "Requires",
      id: "requires",
      canHide: true,
      initHidden: false,
    }),
    column<Row>({
      header: "Conflicts with",
      data: (row) =>
        optionCodesCell(
          row.fieldProposals,
          "optionConflicts",
          [...row.rpo.optionConflicts, ...row.rpo.optionConflictChildren].uniqueBy((o) => o.id),
        ),
      name: "Conflicts with",
      id: "conflicts",
      canHide: true,
      initHidden: false,
    }),
    column<Row>({
      header: "Impact",
      data: (row) => impactCell(row.fieldProposals, row.rpo, row.form),
      name: "Impact",
      id: "impact",
      canHide: true,
      initHidden: false,
    }),
    actionColumn<Row>({
      header: "",
      data: (row) =>
        row.aep ? (
          <ButtonMenu trigger={{ icon: "verticalDots" }} placement="right" items={menuItems(row)} />
        ) : (
          <IconButton
            icon="trash"
            disabled={disabled || disableBasedOnPotentialOperation(row.rpo.canDelete)}
            onClick={() => onRemove(row.rpo)}
          />
        ),
      w: "56px",
      id: "actions",
      name: "Actions",
      clientSideSort: false,
      canHide: false,
    }),
  ];
}

function createFilterDefs(options: PlanPackageOptionsV2_OptionFragment[]): FilterDefs<OptionsFilter> {
  return {
    type: multiFilter({
      label: "Type",
      options: options
        .map((o) => o.type)
        .uniqueBy((t) => t.id)
        .sortBy((t) => t.name),
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    }),
    location: multiFilter({
      label: "Location",
      options: [
        { id: null, name: "None" },
        ...options
          .map((o) => o.readyPlanLocation)
          .compact()
          .uniqueBy((l) => l.id)
          .sortBy((l) => l.name),
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    }),
  };
}

function applyFilter(options: PlanPackageOptionsV2_OptionFragment[], filter: OptionsFilter) {
  return options
    .filter((o) => !filter.type || filter.type.includes(o.type.id))
    .filter((o) => !filter.location || filter.location.includes(o.readyPlanLocation?.id ?? null));
}

/** Shows related option codes, including removal of their owning option. */
function optionCodesCell(
  rows: OptionProposal["fieldProposals"],
  fieldName: "optionPrerequisites" | "optionConflicts",
  options: Pick<PlanPackageOptionsV2_OptionFragment, "id" | "code">[],
): GridCellContent {
  // Not diffed: the AI service doesn't propose `optionPrerequisites` / `optionConflicts` changes, so
  // these only need the removal treatment.
  const value = options.nonEmpty ? <Chips values={options.map((o) => o.code)} /> : "-";
  return {
    content: (
      <AiProposalValue aiProposal={findAiFieldProposal(rows, fieldName)} originalValue={value}>
        {value}
      </AiProposalValue>
    ),
    value: options.map((o) => o.code).join(" "),
  };
}

/** Shows the option's square-footage changes with one bound field per impact line. */
function impactCell(
  rows: OptionProposal["fieldProposals"],
  rpo: PlanPackageOptionsV2_OptionFragment,
  form: ObjectState<OptionRowValue>,
): GridCellContent {
  const pd = optionProgramData(rpo)?.programData;
  const lines = [
    // Extraction only proposes the above-ground half, so the proposed total keeps whatever is below it.
    sqftLine(
      form.sellableSqft,
      rows,
      "sellableAboveGroundSqft",
      "Sellable",
      pd?.sellableSqft,
      pd?.sellableBelowGroundSqft ?? 0,
    ),
    sqftLine(form.netBuildableSqft, rows, "netBuildableSqft", "Net", pd?.netBuildableSqft),
    sqftLine(form.grossBuildableSqft, rows, "grossBuildableSqft", "Gross", pd?.grossBuildableSqft),
  ].compact();
  return {
    content: lines.nonEmpty ? (
      <>{lines.map((line) => line.content)}</>
    ) : (
      <AiProposalValue aiProposal={findAiFieldProposal(rows, "active")} originalValue="-">
        -
      </AiProposalValue>
    ),
    value: lines
      .map((line) => line.value)
      .compact()
      .join(", "),
  };
}

/**
 * One Impact line: a diff while the AI's value is pending review, the saved value otherwise, and
 * nothing at all when there is neither.
 */
function sqftLine(
  field: FieldState<Maybe<string>>,
  rows: OptionProposal["fieldProposals"],
  fieldName: (typeof optionSqftFields)[number],
  label: string,
  current: Maybe<number>,
  /** Added to the proposed value where the label shows a total the AI only proposes part of. */
  addend = 0,
): { content: ReactNode; value: string | undefined } | undefined {
  const proposed = parseAiNumber(rows.find((row) => row.fieldName.value === fieldName)?.fieldValue.value);
  const currentText = impactLine(current, label);
  if (proposed === undefined && !currentText) return undefined;
  return {
    content: (
      <BoundAiTextField
        key={fieldName}
        field={field}
        aiProposal={findAiFieldProposal(rows, fieldName)}
        parser={(raw) => {
          const sqft = parseAiNumber(raw);
          return sqft === undefined ? undefined : (impactLine(sqft + addend, label) ?? `None (${label})`);
        }}
        readOnly
      />
    ),
    value: currentText,
  };
}

function impactLine(sqft: Maybe<number>, label: string): string | undefined {
  return sqft ? `${sqft > 0 ? "+" : ""}${sqft} sf (${label})` : undefined;
}

/**
 * Maps saved option values to read-only text fields alongside their review proposals.
 * Only proposals that key to a row on the table, so none can sit in the form unreachable.
 */
function mapToForm(planPackage: PlanPackageOptionsV2_PlanPackageFragment, aiEnabled: boolean): OptionsFormValue {
  const proposals = indexEntityProposals(
    aiEnabled ? planPackage.aiProposal?.entityProposals : undefined,
    "ReadyPlanOption",
  );
  return {
    options: planPackage.options.map((option) => {
      const pd = optionProgramData(option)?.programData;
      const aep = proposals.get(option.id);
      const isCreated = aep?.type.code === AiEntityProposalType.Create;
      // A created option was built with the values it proposes, so there is no prior value to strike
      // through — but only for the fields it actually proposes. Beam hides an empty original for us.
      const original = (fieldName: string, value: Maybe<string>) =>
        isCreated && aep?.fieldProposals.some((fp) => fp.fieldName === fieldName) ? undefined : value;
      return {
        id: option.id,
        code: original("globalOption", option.code),
        name: original("customName", option.name),
        location: original("location", option.readyPlanLocation?.name ?? "None"),
        // Created options may not have program data yet, so there is no prior impact to strike through.
        sellableSqft: impactLine(pd?.sellableSqft, "Sellable") ?? (isCreated ? undefined : "None (Sellable)"),
        netBuildableSqft: impactLine(pd?.netBuildableSqft, "Net") ?? (isCreated ? undefined : "None (Net)"),
        grossBuildableSqft: impactLine(pd?.grossBuildableSqft, "Gross") ?? (isCreated ? undefined : "None (Gross)"),
      };
    }),
    fieldProposals: flattenEntityProposals([...proposals.values()]),
  };
}

const optionsConfig: ObjectConfig<OptionsFormValue> = {
  ...aiProposalsConfig,
  options: {
    type: "list",
    config: {
      id: { type: "value" },
      code: { type: "value", readOnly: true },
      name: { type: "value", readOnly: true },
      location: { type: "value", readOnly: true },
      sellableSqft: { type: "value", readOnly: true },
      netBuildableSqft: { type: "value", readOnly: true },
      grossBuildableSqft: { type: "value", readOnly: true },
    },
  },
};

/**
 * The Options tab reviews proposals read-only — no cell is editable — so row/bulk actions only move
 * statuses via `resolvePendingAiProposals`, and the save payload is derived from those decisions.
 *
 * Proposal field names:
 * - `globalOption`: Catalog code, e.g. "FIREPLACE". CREATE only.
 * - `location`: Location name, e.g. "Level 1". CREATE only.
 * - `customName`: GraphQL calls this `name`; null clears it back to the catalog name.
 * - `active`: Always "false". DELETE only.
 */

/** One option's entity proposal plus the live form rows for its field proposals. */
type OptionProposal = { aep: AiFormAepFragment; fieldProposals: ObjectState<AiFieldProposalFormValue>[] };

/** Pairs each option's entity proposal with its live statuses, shared by cells, row actions, and the banner. */
function groupProposalsByOption(
  entityProposals: Map<string, AiFormAepFragment>,
  rows: readonly ObjectState<AiFieldProposalFormValue>[],
): Map<string, OptionProposal> {
  const rowsById = new Map(rows.map((row) => [row.id.value, row]));
  return new Map(
    [...entityProposals].map(([optionId, aep]): [string, OptionProposal] => [
      optionId,
      { aep, fieldProposals: aep.fieldProposals.map((fp) => rowsById.get(fp.id)).compact() },
    ]),
  );
}

/** The program data row referencing only this option, i.e. not an elevation pairing or the base plan. */
function optionProgramData(option: PlanPackageOptionsV2_OptionFragment) {
  return option.programDatas.find((rppd) => rppd.readyPlanOptions.length === 1);
}

/**
 * Why this option's proposed removal needs a look before it's applied. No warning column exists on
 * `AiFieldProposal`, so these derive from the option's relationships and live proposal statuses.
 */
function optionWarning(
  option: PlanPackageOptionsV2_OptionFragment,
  rowsByOptionId: Map<string, OptionProposal>,
): { message: string; rows: ObjectState<AiFieldProposalFormValue>[] } | undefined {
  const proposal = rowsByOptionId.get(option.id);
  const rows = proposal?.fieldProposals ?? [];
  // `canDelete` already names what a removal would break, e.g. the options that require this one.
  if (proposal && hasPendingRemoval(proposal.aep.type.code, rows) && option.canDelete.disabledReasons.nonEmpty) {
    return { message: option.canDelete.disabledReasons.map((r) => r.message).join(" "), rows };
  }
  // Also warn on this option when one of its own requirements is being removed elsewhere.
  const removedPrereq = option.optionPrerequisites.find((prereq) => {
    const prereqProposal = rowsByOptionId.get(prereq.id);
    return prereqProposal && hasPendingRemoval(prereqProposal.aep.type.code, prereqProposal.fieldProposals);
  });
  if (removedPrereq) {
    return {
      message: `${option.readyPlanLocation ? `${option.name} ${option.readyPlanLocation.name}` : option.name} is required by this option and was removed from the plan.`,
      rows: rowsByOptionId.get(removedPrereq.id)!.fieldProposals,
    };
  }
  return undefined;
}

/** A DELETE proposal still awaiting a decision. */
function hasPendingRemoval(
  type: AiEntityProposalType,
  rows: readonly ObjectState<AiFieldProposalFormValue>[],
): boolean {
  return type === AiEntityProposalType.Delete && hasPendingProposals(rows);
}
